import { useMutation, useQuery } from 'react-query';
import { datadogLogs } from '@datadog/browser-logs';
import { IProject } from '../../interfaces/project.interface';
import { baseApi, buildAuthHeaders, throwApiError, useRetrieveToken } from './shared';

export const useStripeApi = () => {
  const { retrieveToken, isAuthenticated } = useRetrieveToken();

  const createCheckoutSession = useMutation(
    async (projectData: IProject) => {
      const token = await retrieveToken('createCheckoutSession');
      if (!token) return;

      try {
        const response = await fetch(`${baseApi}/stripe/checkout-session`, {
          method: 'POST',
          headers: buildAuthHeaders(token),
          body: JSON.stringify(projectData),
        });

        if (!response.ok) {
          datadogLogs.logger.error('Failed to create checkout session', {
            name: 'createCheckoutSession',
            response: { status: response.status, statusText: response.statusText },
          });
          throw new Error('Failed to create checkout session');
        }

        return response?.json() as unknown as { clientSecret: string; sessionId: string };
      } catch {
        return { clientSecret: undefined, sessionId: undefined };
      }
    },
    { mutationKey: ['checkout-session'] },
  );

  const usePaymentStatus = (sessionId: string) => {
    return useQuery(
      ['checkout-session', 'payment-status', sessionId],
      async ({ signal }) => {
        const token = await retrieveToken('getPaymentStatus');
        if (!token || !sessionId) return;

        try {
          const response = await fetch(`${baseApi}/stripe/session_status/${sessionId}`, {
            method: 'GET',
            headers: buildAuthHeaders(token),
            signal,
          });

          if (!response.ok) {
            datadogLogs.logger.error('Failed to fetch payment status', {
              name: 'getPaymentStatus',
              response: { status: response.status, statusText: response.statusText },
            });
            throw new Error('Failed to fetch payment status');
          }

          return response.json() as unknown as {
            status: 'complete' | 'open' | 'expired';
            paymentStatus: 'paid' | 'unpaid' | 'no_payment_required';
            clientSecret: string;
            clientEmail: string;
          };
        } catch {
          throw new Error('Failed to fetch payment status');
        }
      },
      {
        enabled: isAuthenticated && !!sessionId,
        refetchInterval: 2000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
      },
    );
  };

  const useCreditCheckoutSession = () => {
    return useMutation(
      async (params: { amount: number; userId: string }) => {
        const token = await retrieveToken('createCreditCheckoutSession');
        if (!token) return;

        try {
          const response = await fetch(`${baseApi}/stripe/credit-checkout-session`, {
            method: 'POST',
            headers: buildAuthHeaders(token),
            body: JSON.stringify(params),
          });

          if (!response.ok) {
            console.error('Failed to create credit checkout session', {
              status: response.status,
              statusText: response.statusText,
            });
            throw new Error('Failed to create credit checkout session');
          }

          return response.json() as Promise<{ clientSecret: string; sessionId: string }>;
        } catch (err) {
          console.error('Error in credit checkout session', err);
          return { clientSecret: undefined, sessionId: undefined };
        }
      },
      { mutationKey: ['credit-checkout-session'] },
    );
  };

  const runMonthlyInvoices = useMutation(async () => {
    const token = await retrieveToken('runMonthlyInvoices');
    if (!token) return;

    const response = await fetch(`${baseApi}/stripe/run-monthly-invoices`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) await throwApiError(response, 'runMonthlyInvoices');

    return response.json();
  });

  return {
    createCheckoutSession,
    usePaymentStatus,
    useCreditCheckoutSession,
    runMonthlyInvoices,
  };
};
